import React, { useEffect, useState } from "react";
import { map } from "lodash";
import { OnboardingStage } from "@/pages/settings/components/contractsSettings/types";
import Text from "@/components/ui/text";
import IconContainer from "@/components/ui/icon-container";
import { ChevronDown } from "@dreamhub/designsystem/icons";
import Collapse from "@/components/ui/collapse";
import StageProgressIcon from "@/components/shared/StageProgressIcon";
import { cn } from "@/utils/utils";
import WorkflowSelectorButton from "@/components/shared/WorkflowSelectorButton";
import OnboardingWorkflowStatusBadge from "./OnboardingWorkflowStatusBadge";

interface Props {
  contractId: string;
  stage: OnboardingStage;
  isCurrentStage: boolean;
  isPreviousStage: boolean;
  hasSelectedWorkflow: boolean;
  selectedWorkflowId: string | undefined;
  onWorkflowSelect: (workflowId: string) => void;
  onStageSelect?: (stageId: string) => void;
  isCompleted?: boolean;
}

const InterStageConnector: React.FC<{
  isCurrentStage: boolean;
  isPreviousStage: boolean;
  stageColor: string | null | undefined;
}> = ({ isCurrentStage, isPreviousStage, stageColor }) => {
  const showColored =
    (isCurrentStage || isPreviousStage) && Boolean(stageColor);
  return (
    <div className="py-1 flex">
      <div className="w-5 flex justify-center">
        <div
          className="w-1 h-4 rounded-full bg-border-field-disabled"
          style={showColored ? { backgroundColor: stageColor! } : undefined}
        />
      </div>
    </div>
  );
};

const OnboardingStageMenuItem: React.FC<Props> = ({
  contractId,
  stage,
  isCurrentStage,
  isPreviousStage,
  hasSelectedWorkflow,
  selectedWorkflowId,
  onWorkflowSelect,
  onStageSelect,
  isCompleted = false
}) => {
  const workflows = stage.workflows ?? [];
  const [isOpen, setIsOpen] = useState(hasSelectedWorkflow || isCompleted);

  useEffect(() => {
    setIsOpen(hasSelectedWorkflow || isCompleted);
  }, [hasSelectedWorkflow, isCompleted]);

  const handleStageClick = (e: React.MouseEvent) => {
    if (workflows.length === 0 && onStageSelect) {
      e.preventDefault();
      e.stopPropagation();
      onStageSelect(stage.id);
    }
  };

  const showColoredRail =
    (isCurrentStage || isPreviousStage) && Boolean(stage.stageColor);

  return (
    <div className="flex flex-col">
      <Collapse
        open={isOpen}
        onOpenChange={workflows.length > 0 ? setIsOpen : undefined}
        trigger={(open) => (
          <div
            className="flex gap-2 py-1 items-center"
            onClick={handleStageClick}
          >
            <StageProgressIcon
              isCurrent={isCurrentStage}
              isPrevious={isPreviousStage}
            />
            <div
              className={cn(
                "group flex items-center justify-between py-2 pl-3 pr-2 rounded-xl cursor-pointer w-full transition-all duration-200 ease-in-out",
                "hover:bg-surface-hover-soft"
              )}
            >
              <Text variant="bodySecondary">{stage.name}</Text>
              {workflows.length > 0 && (
                <IconContainer
                  size="5"
                  color="secondary"
                  className={cn(
                    "transition-all duration-200 ease-in-out opacity-0 group-hover:opacity-100",
                    open ? "rotate-180" : ""
                  )}
                >
                  <ChevronDown />
                </IconContainer>
              )}
            </div>
          </div>
        )}
      >
        <div className="flex flex-col gap-2 ml-8 relative">
          <div
            className="absolute -left-6 inset-0 w-1 rounded-lg bg-border-field-disabled"
            style={{
              background: showColoredRail ? stage.stageColor : undefined
            }}
          />
          {map(workflows, (workflow) => (
            <WorkflowSelectorButton
              key={workflow.id}
              label={workflow.name}
              isSelected={workflow.id === selectedWorkflowId}
              isCurrentStage={isCurrentStage || isCompleted}
              onClick={() => onWorkflowSelect(workflow.id)}
              dataTest={`onboarding-workflow-button-${workflow.id}`}
              rightAdornment={
                <OnboardingWorkflowStatusBadge
                  contractId={contractId}
                  workflow={workflow}
                  isPreviousStage={isPreviousStage}
                />
              }
            />
          ))}
        </div>
      </Collapse>
      {!isOpen && (
        <InterStageConnector
          isCurrentStage={isCurrentStage}
          isPreviousStage={isPreviousStage}
          stageColor={stage.stageColor}
        />
      )}
    </div>
  );
};

export default OnboardingStageMenuItem;
